import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BaseComponent } from '../../base/base.component';
import {
  IDEA_ATTACHMENT_ACCEPT,
  IDEA_ATTACHMENT_MAX_BYTES,
  IDEA_ATTACHMENT_TYPES,
  MAX_IDEA_ATTACHMENTS,
} from './idea-attachments.constants';

/**
 * Seleção de anexos (imagem/PDF) no cadastro da ideia. Só coleta, valida e
 * lista os arquivos; quem envia é o componente pai, depois de criar a ideia.
 * A lista fica no pai (`files`/`filesChange`) para sobreviver à troca de passo.
 */
@Component({
  selector: 'app-idea-attachments',
  templateUrl: './idea-attachments.component.html',
  styleUrls: ['./idea-attachments.component.scss'],
})
export class IdeaAttachmentsComponent extends BaseComponent {
  @Input() files: File[] = [];
  @Input() maxFiles = MAX_IDEA_ATTACHMENTS;
  // false quando o pai já mostra o título (ex.: detalhe da ideia)
  @Input() showLabel = true;
  @Output() filesChange = new EventEmitter<File[]>();

  readonly accept = IDEA_ATTACHMENT_ACCEPT;

  constructor(public router: Router, public translate: TranslateService) {
    super(router, translate);
  }

  onSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    const selected = Array.from(input.files || []);
    // permite escolher o mesmo arquivo de novo depois de removê-lo
    input.value = '';

    const accepted = [...(this.files || [])];
    const rejected: string[] = [];

    selected.forEach((file) => {
      if (!this.isAllowedType(file)) {
        rejected.push(`${file.name}: ${this.translate.instant('attachments.errorType')}`);
      } else if (file.size > IDEA_ATTACHMENT_MAX_BYTES) {
        rejected.push(`${file.name}: ${this.translate.instant('attachments.errorSize')}`);
      } else if (accepted.length >= this.maxFiles) {
        rejected.push(
          `${file.name}: ${this.translate.instant('attachments.errorLimit', { max: this.maxFiles })}`,
        );
      } else {
        accepted.push(file);
      }
    });

    this.filesChange.emit(accepted);

    if (rejected.length) {
      this.showMessage(this.translate.instant('attention'), rejected.join('<br>'));
    }
  }

  onRemove(index: number) {
    this.filesChange.emit(this.files.filter((_, i) => i !== index));
  }

  formatSize(bytes: number): string {
    const kb = bytes / 1024;
    return kb >= 1024 ? `${(kb / 1024).toFixed(2)} MB` : `${Math.max(kb, 0.01).toFixed(2)} KB`;
  }

  private isAllowedType(file: File): boolean {
    const name = (file.name || '').toLowerCase();
    const dot = name.lastIndexOf('.');
    const mimetypes = dot >= 0 ? IDEA_ATTACHMENT_TYPES[name.slice(dot)] : undefined;
    return !!mimetypes && mimetypes.includes((file.type || '').toLowerCase());
  }
}
